import { resolvePeriod } from './period'
import type {
  Comparison,
  PeriodGrain,
  PeriodPreset,
  ResolvedPeriod,
} from './period'
import type { DaypartKey } from './dayparts'

/**
 * The one slice both new surfaces pass to the endpoint (CHG-2026-09-manager-reporting).
 *
 * It is a single object rather than a handful of parameters so a dashboard tile can hand its own
 * filters to the report page it drills into, and the two cannot disagree about what they are
 * showing.
 */
export type ChannelKey = 'SELF_CHECKOUT' | 'STAFF' | 'UNKNOWN' | 'ONLINE'

export interface ReportFilters {
  preset: PeriodPreset
  /**
   * Only read when `preset` is `custom`. The times are absent for a whole-day range, which is the
   * common case and the one every existing link carries.
   */
  customFrom?: string
  customTo?: string
  customFromTime?: string
  customToTime?: string
  customGrain?: PeriodGrain
  /** Empty means every location — the absence of a filter, not a list of every id. */
  restaurantIds: Array<number>
  /**
   * Empty means every article (CHG-2026-09-article-location-filter, D1). A non-empty list narrows
   * the whole page, not just the article breakdown: the same three SKUs answer "how do these sell
   * over time" and "where do they sell".
   */
  skus: Array<string>
  /** `null` means the whole day. A daypart sets the hour window; a free window sets it directly. */
  daypart: DaypartKey | null
  hourFrom: number | null
  hourTo: number | null
  /** Empty means every channel — an explicit empty list would filter everything out. */
  channels: Array<ChannelKey>
  paymentMethods: Array<string>
  categoryId: number | null
}

export function defaultFilters(restaurantIds: Array<number>): ReportFilters {
  return {
    preset: 'today',
    restaurantIds,
    skus: [],
    daypart: null,
    hourFrom: null,
    hourTo: null,
    channels: [],
    paymentMethods: [],
    categoryId: null,
  }
}

export interface ReportQueryParams extends ReportFilters {
  dimension: string
  measure: 'revenue' | 'units'
  /**
   * Overrides the comparison the period would pick for itself. Only the dashboard's second delta
   * on a single day uses it: the period already answers "against what" once, and asking the same
   * window a second question is the exception, not a knob for callers to turn.
   */
  comparison?: Comparison
  limit?: number
  offset?: number
}

/**
 * The query string, and the query key.
 *
 * Empty values are dropped rather than sent as blanks: a `channel=` parameter would look like a
 * filter and reject everything. The same string is the TanStack Query key, so two tiles asking the
 * same question share one cache entry.
 */
export function buildReportParams(
  params: ReportQueryParams,
  now: Date,
): string {
  const period: ResolvedPeriod = resolvePeriod(params.preset, now, {
    from: params.customFrom ?? '',
    to: params.customTo ?? '',
    fromTime: params.customFromTime,
    toTime: params.customToTime,
    grain: params.customGrain,
  })

  const search = new URLSearchParams()
  search.set('dimension', params.dimension)
  search.set('measure', params.measure)
  search.set('comparison', params.comparison ?? period.comparison)
  search.set('from', period.from)
  search.set('to', period.to)
  if (params.restaurantIds.length > 0)
    search.set('restaurantId', params.restaurantIds.join(','))
  if (params.skus.length > 0) search.set('sku', params.skus.join(','))
  if (params.hourFrom != null) search.set('hourFrom', String(params.hourFrom))
  if (params.hourTo != null) search.set('hourTo', String(params.hourTo))
  if (params.channels.length > 0)
    search.set('channel', params.channels.join(','))
  if (params.paymentMethods.length > 0)
    search.set('paymentMethod', params.paymentMethods.join(','))
  if (params.categoryId != null)
    search.set('categoryId', String(params.categoryId))
  if (params.limit != null) search.set('limit', String(params.limit))
  if (params.offset != null && params.offset > 0)
    search.set('offset', String(params.offset))
  return search.toString()
}

export function buildZeroSellerParams(
  filters: ReportFilters,
  now: Date,
  paging?: { limit?: number; offset?: number },
): string {
  const period = resolvePeriod(filters.preset, now, {
    from: filters.customFrom ?? '',
    to: filters.customTo ?? '',
    fromTime: filters.customFromTime,
    toTime: filters.customToTime,
    grain: filters.customGrain,
  })
  const search = new URLSearchParams()
  search.set('from', period.from)
  search.set('to', period.to)
  if (filters.restaurantIds.length > 0)
    search.set('restaurantId', filters.restaurantIds.join(','))
  if (filters.skus.length > 0) search.set('sku', filters.skus.join(','))
  if (filters.categoryId != null)
    search.set('categoryId', String(filters.categoryId))
  if (paging?.limit != null) search.set('limit', String(paging.limit))
  if (paging?.offset != null && paging.offset > 0)
    search.set('offset', String(paging.offset))
  return search.toString()
}
